import * as signalR from '@microsoft/signalr';

import { COREAPI, getUserData } from '@/constants/global';

/**
 * One SignalR connection to api.core's `/liveElectionHub` per (election, party), shared by
 * every consumer that wants it — Live Election's result feed and the shell's cancellation
 * alerts today.
 *
 * Without the ref count each consumer built its own socket and each sent its own `Join`, so
 * a Party Admin on Live Election held two connections to the same group and the hub pushed
 * every event twice. A lease is taken per consumer; the socket stops when the last lease is
 * released.
 */

export type HubStatus = 'live' | 'polling' | 'reconnecting';

type Handler = (payload: unknown) => void;
type Listener = () => void;

export interface IHubConnectionLike {
	on(event: string, handler: Handler): void;
	invoke(method: string, ...args: unknown[]): Promise<unknown>;
	start(): Promise<void>;
	stop(): Promise<void>;
	onreconnecting(cb: () => void): void;
	onreconnected(cb: () => void): void;
	onclose(cb: () => void): void;
}

export type HubFactory = (url: string) => IHubConnectionLike;

const defaultFactory: HubFactory = (url) =>
	new signalR.HubConnectionBuilder()
		.withUrl(url, { accessTokenFactory: () => getUserData().token || '' })
		.configureLogging(signalR.LogLevel.Warning)
		.withAutomaticReconnect([0, 2000, 5000, 10_000, 30_000])
		.build() as unknown as IHubConnectionLike;

let factory: HubFactory = defaultFactory;

/**
 * The hub URL derives from the same env var as the REST client. Hardcoding it (as the VESR
 * hub is) lets REST and the socket silently talk to different backends.
 */
export const liveElectionHubUrl = (): string | null => (COREAPI ? `${COREAPI}/liveElectionHub` : null);

interface IEntry {
	key: string;
	refs: number;
	connection: IHubConnectionLike;
	status: HubStatus;
	handlers: Map<string, Set<Handler>>;
	statusListeners: Set<Listener>;
	reconnectListeners: Set<Listener>;
	stopped: boolean;
}

const entries = new Map<string, IEntry>();

const keyOf = (url: string, electionId: number, partyId: number | null) => `${url}|${electionId}|${partyId ?? 0}`;

const setStatus = (entry: IEntry, status: HubStatus) => {
	if (entry.status === status) return;
	entry.status = status;
	entry.statusListeners.forEach((l) => l());
};

const create = (url: string, electionId: number, partyId: number | null): IEntry => {
	const connection = factory(url);
	const entry: IEntry = {
		key: keyOf(url, electionId, partyId),
		refs: 0,
		connection,
		status: 'polling',
		handlers: new Map(),
		statusListeners: new Set(),
		reconnectListeners: new Set(),
		stopped: false,
	};

	// 0 tells the hub "use my token's party"; only Super Admin names one.
	const join = () =>
		connection.invoke('Join', electionId, partyId === null ? 0 : partyId).catch(() => {
			if (!entry.stopped) setStatus(entry, 'polling');
		});

	connection.onreconnecting(() => setStatus(entry, 'reconnecting'));
	connection.onreconnected(() => {
		setStatus(entry, 'live');
		void join();
		// Events were missed while the socket was down — consumers refetch.
		entry.reconnectListeners.forEach((l) => l());
	});
	connection.onclose(() => setStatus(entry, 'polling'));

	connection
		.start()
		.then(() => {
			if (entry.stopped) return;
			setStatus(entry, 'live');
			void join();
		})
		.catch(() => {
			if (!entry.stopped) setStatus(entry, 'polling');
		});

	return entry;
};

const ensureEventRegistered = (entry: IEntry, event: string) => {
	if (entry.handlers.has(event)) return;
	const set = new Set<Handler>();
	entry.handlers.set(event, set);
	// One SignalR registration per event name, fanning out to every lease's handler.
	entry.connection.on(event, (payload: unknown) => set.forEach((h) => h(payload)));
};

export interface IHubLease {
	getStatus: () => HubStatus;
	on: (event: string, handler: Handler) => () => void;
	onStatus: (listener: Listener) => () => void;
	onReconnected: (listener: Listener) => () => void;
	release: () => void;
}

/** `null` when there is no hub URL or no election — the caller stays on polling. */
export const acquireLiveElectionHub = (electionId: number, partyId: number | null): IHubLease | null => {
	const url = liveElectionHubUrl();
	if (!url || electionId <= 0) return null;
	const key = keyOf(url, electionId, partyId);
	let entry = entries.get(key);
	if (!entry) {
		entry = create(url, electionId, partyId);
		entries.set(key, entry);
	}
	entry.refs += 1;
	const owned = entry;
	const cleanups = new Set<() => void>();
	let released = false;

	return {
		getStatus: () => owned.status,
		on: (event, handler) => {
			ensureEventRegistered(owned, event);
			const set = owned.handlers.get(event)!;
			set.add(handler);
			const off = () => {
				set.delete(handler);
				cleanups.delete(off);
			};
			cleanups.add(off);
			return off;
		},
		onStatus: (listener) => {
			owned.statusListeners.add(listener);
			const off = () => {
				owned.statusListeners.delete(listener);
				cleanups.delete(off);
			};
			cleanups.add(off);
			return off;
		},
		onReconnected: (listener) => {
			owned.reconnectListeners.add(listener);
			const off = () => {
				owned.reconnectListeners.delete(listener);
				cleanups.delete(off);
			};
			cleanups.add(off);
			return off;
		},
		release: () => {
			if (released) return;
			released = true;
			[...cleanups].forEach((off) => off());
			owned.refs -= 1;
			if (owned.refs <= 0) {
				owned.stopped = true;
				entries.delete(owned.key);
				void owned.connection.stop();
			}
		},
	};
};

/** Test seams. */
export const __setHubFactory = (next: HubFactory | null) => {
	factory = next ?? defaultFactory;
};
export const __hubEntryCount = () => entries.size;
export const __resetHubs = () => {
	for (const entry of entries.values()) {
		entry.stopped = true;
		void entry.connection.stop();
	}
	entries.clear();
};
